import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft,
  Wrench,
  Truck,
  Check,
  AlertTriangle,
} from "lucide-react";

const TYPES = [
  { key: "breakdown", label: "Breakdown", color: "bg-red-500",
    desc: "Truck nahi chal raha — immediate help chahiye" },
  { key: "repair", label: "Repair", color: "bg-amber-500",
    desc: "Kuch khrab hai — repair karwana hai" },
  { key: "tyre", label: "Tyre", color: "bg-sky-500",
    desc: "Tyre change / puncture" },
  { key: "oil", label: "Oil / Service", color: "bg-emerald-500",
    desc: "Engine oil / regular service due" },
  { key: "battery", label: "Battery", color: "bg-violet-500",
    desc: "Battery problem / replacement" },
  { key: "other", label: "Other", color: "bg-slate-500",
    desc: "Koi aur issue" },
];

export default function DriverMaintenance() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);   // 1 = type, 2 = vehicle, 3 = form
  const [vehicles, setVehicles] = useState([]);
  const [maintType, setMaintType] = useState("repair");
  const [vid, setVid] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [description, setDescription] = useState("");
  const [odo, setOdo] = useState("");
  const [estimate, setEstimate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/vehicle-ops/driver/vehicles")
      .then(({ data }) => setVehicles(data || []))
      .catch(() => toast.error("Could not load vehicles"));
  }, []);

  const submit = async () => {
    if (!vid) {
      toast.error("Select vehicle");
      return;
    }
    if (!description.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/vehicle-ops/driver/vehicles/${vid}/maintenance`, {
        date: new Date().toISOString().slice(0, 10),
        maint_type: maintType,
        description: description.trim(),
        odometer_km: odo ? parseFloat(odo) : null,
        cost: estimate ? parseFloat(estimate) : 0,
        status: "planned",
      });
      toast.success("Report submitted — admin will action soon");
      nav("/driver");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  /* ---- Step 1: Type ---- */
  if (step === 1) {
    return (
      <div className="space-y-4" data-testid="driver-maintenance">
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/driver")} className="p-2 -ml-2">
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Step 1 of 3
            </div>
            <h1 className="text-xl font-bold text-slate-900"
                style={{ fontFamily: "Chivo" }}>
              Issue Type
            </h1>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => { setMaintType(t.key); setStep(2); }}
              data-testid={`drv-maint-type-${t.key}`}
              className="rounded-2xl bg-white ring-1 ring-slate-200 p-4 active:scale-[0.99] transition flex items-center gap-3"
            >
              <div className={`h-12 w-12 rounded-2xl ${t.color} flex items-center justify-center text-white`}>
                <Wrench className="h-6 w-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-base font-bold text-slate-900">{t.label}</div>
                <div className="text-[11px] text-slate-500">{t.desc}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* ---- Step 2: Vehicle ---- */
  if (step === 2) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setStep(1)} className="p-2 -ml-2">
            <ChevronLeft className="h-5 w-5 text-slate-600" />
          </button>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              Step 2 of 3 · {TYPES.find((t) => t.key === maintType)?.label}
            </div>
            <h1 className="text-xl font-bold text-slate-900"
                style={{ fontFamily: "Chivo" }}>
              Select Vehicle
            </h1>
          </div>
        </div>
        <div className="space-y-2">
          {vehicles.map((v) => (
            <button
              key={v.id}
              onClick={() => setVid(v.id)}
              data-testid={`drv-maint-vehicle-${v.id}`}
              className={`w-full rounded-xl ring-1 p-3 text-left flex items-center gap-3 ${
                vid === v.id ? "ring-bravura-500 bg-bravura-50" : "ring-slate-200 bg-white"
              }`}
            >
              <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Truck className="h-5 w-5 text-slate-700" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">{v.truck_no}</div>
                {(v.make || v.model) && (
                  <div className="text-[11px] text-slate-500">{v.make} {v.model}</div>
                )}
              </div>
              {vid === v.id && <Check className="h-4 w-4 text-bravura-700" />}
            </button>
          ))}
        </div>
        <button
          disabled={!vid}
          onClick={() => { setVehicle(vehicles.find((v) => v.id === vid)); setStep(3); }}
          data-testid="drv-maint-next"
          className="w-full rounded-xl bg-slate-900 text-white py-3 font-semibold disabled:bg-slate-300"
        >
          Continue →
        </button>
      </div>
    );
  }

  /* ---- Step 3: Details ---- */
  return (
    <div className="space-y-4 pb-32" data-testid="drv-maint-form">
      <div className="flex items-center gap-2">
        <button onClick={() => setStep(2)} className="p-2 -ml-2">
          <ChevronLeft className="h-5 w-5 text-slate-600" />
        </button>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            Step 3 of 3 · {vehicle?.truck_no}
          </div>
          <h1 className="text-xl font-bold text-slate-900"
              style={{ fontFamily: "Chivo" }}>
            Describe Issue
          </h1>
        </div>
      </div>

      <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 p-3 text-[11px] text-amber-900">
        <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
        Saaf-saaf likhiye kya problem hai — admin/manager yeh dekh ke action lega.
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Issue description *
        </span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="E.g. Engine se awaaz aa rahi hai, brake oil leak ho raha hai, etc."
          className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
          data-testid="drv-maint-desc"
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Current odometer (km)
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={odo}
            onChange={(e) => setOdo(e.target.value)}
            className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
            data-testid="drv-maint-odo"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Cost estimate ₹ (optional)
          </span>
          <input
            type="number"
            inputMode="numeric"
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
            placeholder="If garage gave you a quote"
            className="mt-1 w-full rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 p-3 z-40">
        <button
          disabled={busy}
          onClick={submit}
          data-testid="drv-maint-submit"
          className="w-full max-w-md mx-auto block rounded-xl bg-slate-900 text-white py-3 font-bold disabled:bg-slate-400"
        >
          {busy ? "Submitting…" : "Submit Report"}
        </button>
      </div>
    </div>
  );
}
